import { capitalCase } from 'change-case'

export type TPlanTier = keyof typeof PlanTiers
export type TPlanTierValue = (typeof PlanTiers)[keyof typeof PlanTiers]
export type TPaymentType = keyof typeof PaymentType
export type TPaymentTypeValue = (typeof PaymentType)[keyof typeof PaymentType]
export type TTheme = keyof typeof Themes
export type TThemeValue = (typeof Themes)[keyof typeof Themes]
export type TFaqCategory = keyof typeof FaqCategories
export type TFaqCategoryValue =
  (typeof FaqCategories)[keyof typeof FaqCategories]
export type TMembershipPeriod = keyof typeof MembershipPeriods
export type TMembershipPeriodValue =
  (typeof MembershipPeriods)[keyof typeof MembershipPeriods]
export type TIcon = keyof typeof Icons
export type TIconValue = (typeof Icons)[keyof typeof Icons]
export type TBenefitsCategory = keyof typeof BenefitsCategories
export type TBenefitsCategoryValue =
  (typeof BenefitsCategories)[keyof typeof BenefitsCategories]
export type TRewardCategory = keyof typeof RewardCategories
export type TRewardCategoryValue =
  (typeof RewardCategories)[keyof typeof RewardCategories]
export type TPrizeCategoryValue =
  (typeof PrizeCategories)[keyof typeof PrizeCategories]
export type TInstantWinPrizeType = keyof typeof InstantWinPrizeType
export type TInstantWinPrizeTypeValue =
  (typeof InstantWinPrizeType)[keyof typeof InstantWinPrizeType]
export type TProductType = (typeof ProductTypes)[keyof typeof ProductTypes]
export type TProductSku = (typeof ProductSku)[keyof typeof ProductSku]
export type TGender = (typeof Genders)[keyof typeof Genders]
export type TCountry = (typeof Countries)[keyof typeof Countries]
export type TInstantWinTypeValue =
  (typeof InstantWinType)[keyof typeof InstantWinType]
export type TInstantWinPrizeTier = keyof typeof InstantWinPrizeTier
export type TInstantWinPrizeTierValue =
  (typeof InstantWinPrizeTier)[keyof typeof InstantWinPrizeTier]

/**
 * Possible list of theme colours.
 */
export const Themes = Object.freeze({
  Transparent: 'transparent',
  Light: 'light',
  Dark: 'dark',
} as const)

/**
 * Possible list of membership periods.
 */
export const MembershipPeriods = Object.freeze({
  Monthly: 'monthly',
  Yearly: 'yearly',
} as const)

/**
 * Possible list of entry types.
 */
export const EntryTypes = Object.freeze({
  Online: 'online',
  Postal: 'postal',
} as const)

/**
 * Possible list of genders.
 */
export const Genders = Object.freeze({
  Male: 'male',
  Female: 'female',
} as const)

/**
 * Possible countries GLP works in.
 */
export const Countries = Object.freeze({
  UnitedKingdom: 'GB',
  Ireland: 'IE',
  IsleOfMan: 'IM',
  Jersey: 'JE',
  Canada: 'CA',
} as const)

/**
 * A flipped object of `Countries` with keys being country codes.
 */
export const CountryOptions = Object.freeze(
  Object.fromEntries(
    Object.entries(Countries).map(([country, code]) => [
      code,
      capitalCase(country),
    ])
  )
)

/**
 * Canadian provinces and territories, keyed by their two-letter code (the
 * stored value) mapped to display name. Used for the Canada-style checkout
 * address form (True North).
 */
export const CanadianProvinces = Object.freeze({
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
} as const)

/**
 * Possible list of prize result categories.
 */
export const PrizeResultCategories = Object.freeze({
  Winners: 'Hall of Fame',
  Draws: 'Live Draws',
  InstantResults: 'Instant Prize Winners',
} as const)

/**
 * Possible prize categories.
 */
export const PrizeCategories = Object.freeze({
  AllPrizes: 'All prizes',
  AllGames: 'All games',
  Cash: 'Cash',
  DreamCars: 'Dream cars',
  Experiences: 'Experiences',
  Featured: 'Featured',
  GetAways: 'Get aways',
  InstantWins: 'Instant wins',
  LuxuryItem: 'Luxury Item',
  Technology: 'Technology',
  MysteryBoxes: 'Mystery boxes',
  InstantSpins: 'Instant Spins',
  InstantPacks: 'Instant Packs',
  SmallPrizesLeft: 'Small Prizes Left',
} as const)

export const RewardCategories = Object.freeze({
  FoodAndDrink: 'food-and-drink',
  Entertainment: 'entertainment',
  Fashion: 'fashion',
  Tech: 'tech',
  Retail: 'retail',
  DrawBoosts: 'draw-boost',
  InstantWin: 'instant_win',
  MysteryBox: 'mystery_box',
  InstantSpin: 'instant_spin',
  InstantPack: 'instant_pack',
} as const)

/**
 * Possible benefit categories.
 */
export const BenefitsCategories = Object.freeze({
  Discounts: 'Discounts',
  FoodAndDrink: 'Food & Drink',
  Entertainment: 'Entertainment',
} as const)

/**
 * Possible FAQ categories.
 */
export const FaqCategories = Object.freeze({
  HowItWorks: 'how-it-works',
  WhoAreWe: 'who-are-we',
  WinnersAndPrizes: 'winners-and-prizes',
  EntriesAndTickets: 'entries-and-tickets',
} as const)

/**
 * Possible FAQ routes slugs '/faq/:slug'.
 */
export const FaqSlugs = Object.freeze({
  AboutGoodLifePlus: 'about',
  MembershipPlans: 'membership-plans',
  EnteringDraws: 'entering-draws',
  InstantWinDraws: 'instant-win-draws',
  DailyPlayLoyalty: 'daily-play-loyalty',
  WinningPayouts: 'winning-payouts',
  DepositsWithdrawals: 'deposits-withdrawals',
  CreditBackSiteCredit: 'credit-back-site-credit',
  WeeklyRaceGoodLifePoints: 'weekly-race-good-life-points',
  Rewards: 'rewards',
  ReferralsGoldenBox: 'referrals-golden-box',
  GoodCausesCharity: 'good-causes-charity',
  TrustSecurityPrivacy: 'trust-security-privacy',
  TroubleshootingSupport: 'troubleshooting-support',
  ComplaintsPolicy: 'complaints-policy',
  AccountManagement: 'account-management',
  MembershipsBilling: 'memberships-billing',
  RewardsDiscountsVouchers: 'rewards-discounts-vouchers',
  PointsRedemptions: 'points-redemptions',
  Giveaways: 'giveaways',
  ManagingAccount: 'managing-account',
  PaymentsCancellationsRefunds: 'payments-cancellations-refunds',
  TechnicalSupport: 'technical-support',
  TrustPrivacySecurity: 'trust-privacy-security',
  ComplaintsContact: 'complaints-contact',
} as const)

/**
 * Possible icons.
 */
export const Icons = Object.freeze({
  AccordionArrow: 'AccordionArrow',
  AccountManage: 'AccountManage',
  ArrowDown: 'ArrowDown',
  ArrowDownThin: 'IconArrowDownThin',
  ArrowLeft: 'ArrowLeft',
  ArrowRight: 'ArrowRight',
  Balance: 'Balance',
  Bell: 'Bell',
  Building: 'Building',
  Calendar: 'Calendar',
  CalendarStar: 'CalendarStar',
  Car: 'DreamCars',
  Card: 'Card',
  CardPack: 'CardPack',
  Cash: 'Cash',
  CashNote: 'CashNote',
  Chart: 'Chart',
  Check: 'Check',
  ChevronDown: 'ChevronDown',
  ChevronUp: 'ChevronUp',
  Clock: 'Clock',
  ClockFilled: 'ClockFilled',
  Clover: 'Clover',
  CloverOutlined: 'CloverOutlined',
  Crown: 'Crown',
  Database: 'Database',
  Diamond: 'Diamond',
  Diversity: 'Diversity',
  Dollar: 'Dollar',
  Edit: 'Edit',
  Engine: 'Engine',
  Entries: 'Entries',
  Envelope: 'Envelope',
  Error: 'Error',
  ErrorGoogle: 'ErrorGoogle',
  ErrorSolid: 'ErrorSolid',
  EventAvailable: 'EventAvailable',
  Experiences: 'Experiences',
  Food: 'Food',
  Football: 'Football',
  GearShift: 'GearShift',
  GetAways: 'GetAways',
  Gift: 'Gift',
  GoodLifeCoin: 'GoodLifeCoin',
  HandHeart: 'HandHeart',
  HeartBroken: 'HeartBroken',
  Home: 'Home',
  Information: 'Information',
  LeaderBoard: 'LeaderBoard',
  Lightening: 'Lightening',
  LighteningOutlined: 'LighteningOutlined',
  Lock: 'Lock',
  LuxuryItem: 'Luxury Item',
  Mastercard: 'Mastercard',
  Megaphone: 'Megaphone',
  Member: 'Member',
  MemberPrizes: 'MemberPrizes',
  MembersZone: 'MembersZone',
  Message: 'Message',
  Mileage: 'Mileage',
  MobileTablet: 'MobileTablet',
  MoneyBag: 'MoneyBag',
  MoneyBagFill: 'MoneyBagFill',
  MoneyBox: 'MoneyBox',
  Packs: 'Packs',
  Padlock: 'Padlock',
  Plans: 'Plans',
  Play: 'Play',
  Present: 'Present',
  Prize: 'Prize',
  PrizeSack: 'PrizeSack',
  Profile: 'Profile',
  Question: 'Question',
  Reload: 'Reload',
  Reward: 'Reward',
  Ribbon: 'Ribbon',
  Setting: 'Setting',
  Share: 'Share',
  Signal: 'Signal',
  SlotMachine: 'SlotMachine',
  Socials: 'Socials',
  Spaceship: 'Spaceship',
  Sparkle: 'Sparkle',
  SparkleFilled: 'SparkleFilled',
  SpeakerOff: 'SpeakerOff',
  SpeakerOn: 'SpeakerOn',
  SpeedGauge: 'SpeedGauge',
  SpecPlaceholder: 'SpecPlaceholder',
  Star: 'Star',
  StarOutlined: 'StarOutlined',
  StarRefresh: 'StarRefresh',
  StarTick: 'StarTick',
  Support: 'Support',
  Technology: 'Technology',
  Telephone: 'Telephone',
  Ticket: 'Ticket',
  TicketStar: 'TicketStar',
  TicketTick: 'TicketTick',
  Timer: 'Timer',
  Times: 'Times',
  Transfer: 'Transfer',
  Transmission: 'Transmission',
  Trophy: 'Trophy',
  TV: 'TV',
  Visa: 'Visa',
  Watch: 'Watch',
  Whatsapp: 'Whatsapp',
  Winners: 'Winners',
  Year: 'Year',
} as const)

/**
 * Prize highlights only carry an icon + a single value from the CMS (no
 * separate label field) - these known vehicle-spec icons get a fixed label
 * so the value can be shown underneath/alongside it.
 */
export const HighlightIconLabels: Partial<Record<TIcon, string>> = {
  Year: 'Year',
  Mileage: 'Mileage',
  Engine: 'Engine',
  Transmission: 'Transmission',
  Sparkle: 'Colour',
  Car: 'Make & Model',
  SpecPlaceholder: 'Other Main Spec',
}

/**
 * Possible plan tiers (values are numbers for plan comparison).
 *
 * Note: Although some tier values are the same, the order is important for comparison.
 */
export const PlanTiers = Object.freeze({
  postal: 0,
  lite: 1,
  standard: 2,
  'standard-plus': 2,
  unlimited: 2,
  premium: 3,
  platinum: 3,
  gold: 3,
  vip: 4,
  legend: 5,
} as const)

export const NamedPlanTiers = Object.freeze({
  Postal: 'postal',
  Lite: 'lite',
  Standard: 'standard',
  StandardPlus: 'standard-plus',
  Unlimited: 'unlimited',
  Premium: 'premium',
  Platinum: 'platinum',
  Gold: 'gold',
  Vip: 'vip',
  Legend: 'legend',
} as const)

/**
 * Possible benefit codes.
 */
export const BenefitCodes = Object.freeze({
  Blacksheep: 'blacksheep',
  BurgerKing: 'burger-king',
  CaffeNero: 'caffe-nero',
  Chiquito: 'chiquito',
  Costa: 'costa',
  Deliveroo: 'deliveroo',
  Dominos: 'dominos',
  FrankieBennys: 'frankie-bennys',
  GourmetBurgerKitchen: 'gourmet-burger-kitchen',
  Greggs: 'greggs',
  KrispyKreme: 'krispy-kreme',
  MarksAndSpencer: 'mands',
  PizzaExpress: 'pizza-express',
  PizzaHut: 'pizza-hut',
  Starbucks: 'starbucks',
  Tastecard: 'tastecard',
  Vue: 'vue',
  Zizzi: 'zizzi',
} as const)

/**
 * Possible Instant Win prize types.
 *
 * Note: Order of these values are important and must match those in Checkout::EnumHelper.
 */
export const InstantWinPrizeType = Object.freeze({
  Cash: 'cash',
  Entry: 'entry',
  Credit: 'credit',
  Item: 'item',
  Crypto: 'crypto',
} as const)

export const SubscriptionStatus = Object.freeze({
  Active: 'active',
  InTrial: 'in_trial',
  NonRenewing: 'non_renewing',
  Cancelled: 'cancelled',
  Future: 'future',
  Paused: 'paused',
} as const)

export type TSubscriptionStatus =
  (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus]

/**
 * Possible prize teirs.
 */
export const InstantWinPrizeTier = Object.freeze({
  A: 1,
  B: 2,
  C: 3,
  D: 4,
  E: 5,
  S: 6,
  SS: 7,
  /**
   * A special tier to represent a non-winning outcome.
   */
  NonWinner: 99,
} as const)

/**
 * Possible provider names.
 */
export const PaymentProviderName = Object.freeze({
  Chargebee: 'chargebee',
  Solidgate: 'solidgate',
} as const)

/**
 * Possible payment providers.
 */
export const PaymentProviders = Object.freeze({
  Chargebee: 'chargebee',
  Solidgate: 'solidgate',
} as const)

/**
 * Possible payment types.
 */
export const PaymentType = Object.freeze({
  Subscription: 'subscription',
  InstantWin: 'instant_win',
  PaymentUpdate: 'payment_update',
  Product: 'product',
} as const)

/**
 * Possible product types.
 */
export const ProductTypes = Object.freeze({
  InstantWinDraw: 'InstantWinDraw',
  Subscription: 'Subscription',
  Deposit: 'Deposit',
} as const)

/**
 * Possible product SKUs for the basket system.
 */
export const ProductSku = Object.freeze({
  InstantWinDraw: 'InstantWinDraw',
  Subscription: 'Subscription',
  DepositCustom: 'WALLET_CUSTOM',
  DepositWelcomeCustom100: 'welcome-deposit-100',
  DepositPack25: 'CREDITS_PACK_25',
  DepositPack50: 'CREDITS_PACK_50',
  PassBasic: 'pass-basic',
  PassStandard: 'pass-standard',
  PassVip: 'pass-vip',
  PassLegend: 'pass-legend',
} as const)

/**
 * Possible Instant Win types
 */
export const InstantWinType = Object.freeze({
  Standard: 1,
  MysteryBox: 2,
  InstantSpin: 3,
  InstantPack: 4,
} as const)

/**
 * User credit types
 */
export const CreditTypes = Object.freeze({
  Bonus: 'bonus',
  Cashable: 'cashable',
  InstantWinCashable: 'iw_cashable',
} as const)

/**
 * Transaction types
 */
export const TransactionTypes = Object.freeze({
  /** Transaction is a credit reward for referring a friend (old system) */
  CreditReferral: 'creditReferral',
  /** Transaction is a result of winning credit in a draw or instant game */
  CreditWin: 'creditWin',
  /** Transaction is a result of winning credit in a frequency game (e.g. Daily Spins) */
  CreditFrequencyGame: 'creditFrequencyGame',
  /** Transaction is a result of winning credit by appearing at the top of the leaderboard feature */
  CreditLeaderboard: 'creditLeaderboard',
  /** Transaction is a result of credit awarded back from a purchase */
  CreditCreditback: 'creditCreditback',
  /** Transaction is a credit reward for referring a friend (Referral Box scheme) */
  CreditReferralBox: 'creditReferralBox',
  /** Transaction is a result of credit awarded for renewing a subscription */
  CreditLoyalty: 'creditLoyalty',
  /** Transaction is a result of customer service adding or removing credit on the account */
  CreditCustomerService: 'creditCustomerService',
  /** Transaction is a result of winning cashable credit in a draw or instant game */
  CashWin: 'cashWin',
  /** Transaction is a result of customer service adding cashable credit to the account */
  CashCustomerService: 'cashCustomerService',
  /** Transaction is the result of withdrawing cashable credit from the account */
  CreditCashout: 'creditCashout',
  /** Transaction is a result of purchasing entries for a draw or an instant game using credits */
  CreditPayment: 'creditPayment',
  /** Transaction is a general payment */
  Payment: 'payment',
  /** Transaction is for an instant game */
  PaymentInstantWin: 'paymentInstantWin',
  /** Transaction is a subscription */
  PaymentSubscription: 'paymentSubscription',
} as const)

export const DrawEntryTypes = Object.freeze({
  /** Entry is a subscription to a prize draw */
  Subscription: 'subscription',
  /** Entry is from a purchase (i.e Instant Game) */
  Purchase: 'purchase',
} as const)

export const BalanceSummaryItemTypes = Object.freeze({
  Cashout: 'Cashout',
  Referral: 'Referral',
  Win: 'Win',
  FrequencyGame: 'FrequencyGame',
  Payment: 'Payment',
  Leaderboard: 'Leaderboard',
  Creditback: 'Creditback',
  ReferralBox: 'ReferralBox',
  Loyalty: 'Loyalty',
  CustomerService: 'CustomerService',
} as const)

export const BalanceSummaryWithdrawalStatus = Object.freeze({
  Pending: 'pending',
  Fulfilled: 'fulfilled',
  Cancelled: 'cancelled',
} as const)

export const EntryDetailItemTypes = Object.freeze({
  Subscription: 'subscription',
  Loyalty: 'loyalty',
  DrawSpecific: 'draw_specific',
  Lead: 'lead',
  Purchase: 'purchase',
  External: 'external',
} as const)

/**
 * Possible entry filter types for the member entries view.
 * These are used to filter the entries shown based on their source.
 */
export const EntryFilterType = Object.freeze({
  Subscriptions: 'subscriptions',
  InstantGames: 'instantgames',
} as const)
